import { Component } from 'react'
import { Field, FormProps, reduxForm } from 'redux-form'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import * as styles from 'styles/molecules/forms/LinkageForm.css'
import Button from 'components/features/pfm/Button/Button'
import { AccountCategoryId } from 'interfaces/meta'
import { State } from 'interfaces/state'
import TextFieldWrapped, { TextFieldWrappedOwnProps } from 'components/atoms/inputs/TextFieldWrapped'
import OldSelectWrapped, { OldSelectOwnProps } from 'components/atoms/inputs/OldSelectWrapped'
import { formatSignedCurrency, getSaveButtonFormProps, onFormSubmit, parseSignedCurrency, shouldAsyncValidate } from 'helpers/formHelpers'
import { Option } from 'interfaces/component'
import manualAccountSchema, { MANUAL_ACCOUNT_NAME_MAX_LENGTH } from 'schemas/manualAccountSchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import { ManualAccountFormData, MixedCompositeId } from 'interfaces/linkage'
import { extractLinkageEntityFromCid } from 'helpers/linkageHelpers'
import { createSelector, defaultMemoize } from 'reselect'
import { manualAccountsCategoryOptionsSelector } from 'selectors/linkageSelectors'
import { connect } from 'react-redux'
import { requestLinkageSessionActionCreator } from 'actions/linkageActions'
import withMobile, { WithMobileProps } from 'hocs/withMobile/withMobile'
import { FORM_MANUAL_ACCOUNT } from 'constants/forms'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { isMobileOrTabletSelector } from 'selectors/viewportSelectors'

type ManualAccountFormFormProps = FormProps<ManualAccountFormData, {}, State>

type ManualAccountFormStateProps = {
  categoryOptions: Option<AccountCategoryId>[]
  balanceAutofocus: boolean
}

type ManualAccountFormDispatchProps = {
  linkAnotherAccount: () => void
}

export type ManualAccountFormOwnProps = {
  cid: MixedCompositeId
  initialValues: ManualAccountFormData
  onCancel?: () => void
}

type ManualAccountFormProps =
  & WithMobileProps
  & ManualAccountFormFormProps
  & ManualAccountFormStateProps
  & ManualAccountFormDispatchProps
  & ManualAccountFormOwnProps

const NAME_FIELD_PROPS: TextFieldWrappedOwnProps = {
  label: 'Name',
  autoComplete: 'off',
  maxLength: MANUAL_ACCOUNT_NAME_MAX_LENGTH,
  qaMarker: 'name',
}

const BALANCE_FIELD_PROPS: TextFieldWrappedOwnProps = {
  label: 'Balance',
  type: 'tel',
  autoComplete: 'off',
  mask: createNumberMask({ prefix: '$', allowNegative: true }),
  // from "-$999,999,999" to "$999,999,999"
  maxLength: 13,
  qaMarker: 'balance',
  qaType: 'tel',
}

const getBalanceFieldProps = defaultMemoize((autoFocus: boolean) => {
  return {
    ...BALANCE_FIELD_PROPS,
    autoFocus,
  }
})

@reduxForm({
  form: FORM_MANUAL_ACCOUNT,
  onSubmit: onFormSubmit,
  shouldAsyncValidate,
  asyncValidate: asyncValidator(manualAccountSchema),
})
class ManualAccountFormComponent extends Component<ManualAccountFormProps> {
  render () {
    const {
      categoryOptions,
      handleSubmit,
      cid,
      balanceAutofocus,
    } = this.props

    const categoryFieldProps: OldSelectOwnProps<AccountCategoryId> = {
      label: 'Account Type',
      theme: 'white',
      options: categoryOptions,
      qaMarker: 'category',
    }

    const entityType = extractLinkageEntityFromCid(cid)
    if (!entityType) {
      return null
    }

    const isEditing = entityType.id !== null
    const submitButtonLabel = isEditing
      ? 'Update Your Account'
      : 'Finish'
    const addAnotherAccountButton = isEditing
      ? null
      : this.renderAddAnotherAccountButton()

    return (
      <form
        className={ styles.accountLinkageForm }
        onSubmit={ handleSubmit }
      >
        <section className={ styles.fieldsContainer }>
          <h4 className={ styles.name }>
            Manual Account
          </h4>
          <div className={ styles.fieldList }>
            <div className={ styles.fieldListItem }>
              <Field
                name='category'
                component={ OldSelectWrapped }
                props={ categoryFieldProps }
              />
            </div>
            <div className={ styles.fieldListItem }>
              <Field
                name='name'
                component={ TextFieldWrapped }
                props={ NAME_FIELD_PROPS }
              />
            </div>
            <div className={ styles.fieldListItem }>
              <Field
                name='amount'
                component={ TextFieldWrapped }
                props={ getBalanceFieldProps(balanceAutofocus) }
                normalize={ parseSignedCurrency }
                format={ formatSignedCurrency }
              />
            </div>
          </div>
          <br />
          <div className={ styles.buttonWrapper }>
            <Button
              { ...getSaveButtonFormProps(this.props) }
              text={ submitButtonLabel }
              fullWidth
              submit
            />
          </div>
          { addAnotherAccountButton }
        </section>
      </form>
    )
  }

  renderAddAnotherAccountButton () {
    const { linkAnotherAccount, onCancel } = this.props

    return onCancel ? (
      <div className={ styles.buttonWrapper }>
        <Button
          fullWidth
          style='hollow'
          onClick={ onCancel }
        >
          Cancel
        </Button>
      </div>
    ) : (
      <div className={ styles.buttonWrapper }>
        <Button
          { ...getSaveButtonFormProps(this.props) }
          submit
          fullWidth
          theme='lightBlue'
          onClick={ linkAnotherAccount }
        >
          Add Another Account
        </Button>
      </div>
    )
  }
}

const createMapStateToProps = () => {
  return createSelector(
    manualAccountsCategoryOptionsSelector,
    createOwnPropsSelector<ManualAccountFormOwnProps>(),
    isMobileOrTabletSelector,
    (categoryOptions, ownProps, isMobileOrTablet): ManualAccountFormStateProps => {
      const balanceAutofocus = Boolean(!isMobileOrTablet && ownProps.initialValues.amount.absValue)

      return {
        categoryOptions,
        balanceAutofocus,
      }
    },
  )
}

const mapDispatchToProps: ManualAccountFormDispatchProps = {
  linkAnotherAccount: () => requestLinkageSessionActionCreator('mba'),
}

const ManualAccountFormInternal = connect<
  ManualAccountFormStateProps, ManualAccountFormDispatchProps, ManualAccountFormOwnProps & WithMobileProps
>(createMapStateToProps, mapDispatchToProps)(ManualAccountFormComponent)

export const ManualAccountForm = withMobile(ManualAccountFormInternal)
